import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Lightbulb } from "lucide-react";
import { getOfficialLink } from "@/config/actionConfig";

// Configuration contextuelle par type de document
export const DOC_TYPE_INTRO: Record<string, { 
  intro: string; 
  tip?: string;
  requiresAppointment?: boolean;
}> = {
  identity: {
    intro: "Pour renouveler votre carte d'identité, vous devez d'abord effectuer une pré-demande en ligne sur le site ANTS, puis prendre rendez-vous en mairie.",
    tip: "Anticipez ! Les délais peuvent atteindre 2-3 mois en période estivale.",
    requiresAppointment: true,
  },
  passport: {
    intro: "Le renouvellement du passeport se fait via une pré-demande ANTS suivie d'un rendez-vous en mairie. N'oubliez pas le timbre fiscal.",
    tip: "Vérifiez que votre photo est conforme aux normes avant de vous déplacer.",
    requiresAppointment: true,
  },
  gray_card: {
    intro: "La demande de carte grise se fait intégralement en ligne sur le site ANTS ou via un professionnel agréé.",
    tip: "Vous recevrez d'abord un Certificat Provisoire d'Immatriculation (CPI) valable 1 mois.",
    requiresAppointment: false,
  },
  driver_license: {
    intro: "Pour renouveler ou faire un duplicata de votre permis de conduire, effectuez votre demande en ligne sur ANTS.",
    tip: "En cas de perte ou vol, faites d'abord une déclaration.",
    requiresAppointment: false,
  },
  health: {
    intro: "La mise à jour de votre carte Vitale peut se faire en pharmacie ou en borne dans votre CPAM.",
    tip: "Pensez à mettre à jour votre carte après chaque changement de situation.",
    requiresAppointment: false,
  },
  residence_permit: {
    intro: "Le renouvellement du titre de séjour doit être demandé 2 à 4 mois avant son expiration auprès de votre préfecture.",
    tip: "Prenez rendez-vous le plus tôt possible, les créneaux partent vite.",
    requiresAppointment: true,
  },
  vehicle_insurance: {
    intro: "Pour changer d'assurance auto, vous pouvez résilier à tout moment après un an de contrat grâce à la loi Hamon.",
    tip: "Demandez votre relevé d'informations à votre assureur actuel.",
    requiresAppointment: false,
  },
  technical_control: {
    intro: "Le contrôle technique doit être effectué dans les 6 mois précédant la date anniversaire des 4 ans du véhicule, puis tous les 2 ans.",
    tip: "Comparez les prix des centres agréés, ils peuvent varier significativement.",
    requiresAppointment: true,
  },
  kbis: {
    intro: "L'extrait Kbis peut être commandé en ligne sur Infogreffe ou MonIdenum. C'est une démarche 100% en ligne.",
    tip: "Pour les dirigeants, l'extrait Kbis numérique est gratuit sur MonIdenum.",
    requiresAppointment: false,
  },
  professional_card: {
    intro: "Le renouvellement de votre carte professionnelle dépend de votre secteur d'activité. Consultez l'organisme émetteur.",
    tip: "Anticipez de 2-3 mois avant l'expiration.",
    requiresAppointment: false,
  },
  work_permit: {
    intro: "L'autorisation de travail est généralement liée à votre titre de séjour. Vérifiez les conditions auprès de la préfecture.",
    tip: "Votre employeur doit souvent être impliqué dans la démarche.",
    requiresAppointment: true,
  },
};

interface ActionIntroCardProps {
  docType: string | null;
}

export function ActionIntroCard({ docType }: ActionIntroCardProps) {
  if (!docType) return null;
  
  const config = DOC_TYPE_INTRO[docType];
  const officialLink = getOfficialLink(docType);
  
  if (!config && !officialLink) return null;

  return (
    <Card className="bg-muted/30 border-dashed">
      <CardContent className="p-4 space-y-3">
        {config?.intro && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {config.intro}
          </p>
        )}
        
        {officialLink && (
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-2"
            onClick={() => window.open(officialLink.url, '_blank')}
          >
            <ExternalLink className="w-4 h-4" />
            {officialLink.label}
          </Button>
        )}
        
        {config?.tip && (
          <div className="flex items-start gap-2 text-xs text-muted-foreground/80 italic pt-1">
            <Lightbulb className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
            <span>{config.tip}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
